import { createContext, useContext } from "react";
import type {
  INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
  IActivityOccurrenceOrGroup,
} from "@medoma/models/activities";
import type { IVisit } from "@medoma/models/visits";
import { z } from "zod";

const selectedItemTypes = [
  "itemToPlan",
  "plannedRoute",
  "plannedVisit",
  "plannedActivityOccurrenceOrGroup",
] as const;

export const selectedItemTypeSchema = z.enum(selectedItemTypes);

export type ISelectedItem =
  | {
      type: typeof selectedItemTypeSchema.enum.itemToPlan;
      item: IActivityOccurrenceOrGroup;
    }
  | {
      type: typeof selectedItemTypeSchema.enum.plannedRoute;
      routeId: string;
      shiftId: number;
    }
  | {
      type: typeof selectedItemTypeSchema.enum.plannedVisit;
      item: IVisit;
      routeId: string;
      shiftId: number;
    }
  | {
      type: typeof selectedItemTypeSchema.enum.plannedActivityOccurrenceOrGroup;
      item: INonRoutableEmployeePerformedActivityOccurrenceOrGroup;
      shiftId: number;
    };

type SelectionContextType = {
  selectedItem: ISelectedItem | undefined;
  setSelectedItem: (_selectedItem: ISelectedItem | undefined) => void;
  clearSelection: () => void;
  boundsShowingAllPatients:
    | readonly [{ lat: number; lng: number }, { lat: number; lng: number }]
    | undefined;
  setBoundsShowingAllPatients: (
    _boundsShowingAllPatients:
      | readonly [{ lat: number; lng: number }, { lat: number; lng: number }]
      | undefined,
  ) => void;
};

export const SelectionContext = createContext<SelectionContextType>({
  selectedItem: undefined,
  setSelectedItem: () => {},
  clearSelection: () => {},
  boundsShowingAllPatients: undefined,
  setBoundsShowingAllPatients: () => {},
});

export const useSelection = () => useContext(SelectionContext);
